import { TextTypeEnum, VignetteTextTypeEnum } from '@lite-workspace/dto-fotoclass-templates';

/** Every text type a drawable can carry. */
export type AnyTextType = TextTypeEnum | VignetteTextTypeEnum;

export enum TextScope {
  /** One value for the whole project; authored in project settings. */
  SHARED = 'shared',
  /** Belongs to this album alone; authored in the book model. */
  ALBUM = 'album',
}

export enum TemplateTextOrigin {
  /** The template's own copy is a usable default and seeds the project. */
  DEFAULT = 'default',
  /** The template's copy is filler a human must replace; seeding it would publish a stranger's data. */
  SAMPLE = 'sample',
}

export interface TextTypeDeclaration {
  readonly scope: TextScope;
  readonly templateText: TemplateTextOrigin;
}

/**
 * The scope of every text type, declared exhaustively: `Record<AnyTextType, …>` fails to compile
 * until a newly added type states where its value lives and whether a template may seed it.
 */
export const TEXT_TYPE_DECLARATIONS: Readonly<Record<AnyTextType, TextTypeDeclaration>> = {
  [TextTypeEnum.SCHOOL]: { scope: TextScope.SHARED, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.CLASS]: { scope: TextScope.SHARED, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.CITY]: { scope: TextScope.SHARED, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.GRADUATION_YEAR]: { scope: TextScope.SHARED, templateText: TemplateTextOrigin.SAMPLE },

  // Only the title is a standing design word. The subtitles are data slots wearing a caption:
  // their baked «КЛАСС»/«ВЫПУСК» is filler for the class and year, and albums do type their own.
  [VignetteTextTypeEnum.TITLE]: { scope: TextScope.SHARED, templateText: TemplateTextOrigin.DEFAULT },
  [VignetteTextTypeEnum.SUBTITLE]: { scope: TextScope.SHARED, templateText: TemplateTextOrigin.SAMPLE },
  [VignetteTextTypeEnum.SUBTITLE2]: { scope: TextScope.SHARED, templateText: TemplateTextOrigin.SAMPLE },
  [VignetteTextTypeEnum.SUBTITLE3]: { scope: TextScope.SHARED, templateText: TemplateTextOrigin.SAMPLE },

  [TextTypeEnum.FREE_TEXT]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.PERSON_FIRST_NAME]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.PERSON_LAST_NAME]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.PERSON_FULL_NAME]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.PERSON_PATRONYMIC]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.PERSON_GIVEN_PATRONYMIC]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.PERSON_QUOTE]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [TextTypeEnum.PERSON_SUBJECT]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [VignetteTextTypeEnum.LEAD_QUOTE]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [VignetteTextTypeEnum.LEAD_SIGNATURE]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
  [VignetteTextTypeEnum.CLASS_QUOTE]: { scope: TextScope.ALBUM, templateText: TemplateTextOrigin.SAMPLE },
};

const ALL_TEXT_TYPES = Object.keys(TEXT_TYPE_DECLARATIONS) as AnyTextType[];

function typesWhere(predicate: (declaration: TextTypeDeclaration) => boolean): ReadonlySet<string> {
  return new Set<string>(ALL_TEXT_TYPES.filter(textType => predicate(TEXT_TYPE_DECLARATIONS[textType])));
}

/** Shared text: one value per project, stored in project settings, never on the album. */
export const SHARED_TEXT_TYPES: ReadonlySet<string> = typesWhere(d => d.scope === TextScope.SHARED);

/** Album text: the album's own, stored in the book model. */
export const ALBUM_TEXT_TYPES: ReadonlySet<string> = typesWhere(d => d.scope === TextScope.ALBUM);

/** Shared types a template may seed into a fresh project. */
export const TEMPLATE_SEEDED_TEXT_TYPES: ReadonlySet<string> = typesWhere(
  d => d.scope === TextScope.SHARED && d.templateText === TemplateTextOrigin.DEFAULT,
);

const VIGNETTE_TYPES: ReadonlySet<string> = new Set<string>(Object.values(VignetteTextTypeEnum));

/** Shared text printed on the cover — the two families the album sync offers as separate choices. */
export const COVER_SHARED_TEXT_TYPES: readonly string[] = [...SHARED_TEXT_TYPES].filter(t => !VIGNETTE_TYPES.has(t));

export const VIGNETTE_SHARED_TEXT_TYPES: readonly string[] = [...SHARED_TEXT_TYPES].filter(t => VIGNETTE_TYPES.has(t));

export function isSharedTextType(textType: string | undefined): boolean {
  return !!textType && SHARED_TEXT_TYPES.has(textType);
}

export function isAlbumTextType(textType: string | undefined): boolean {
  return !!textType && ALBUM_TEXT_TYPES.has(textType);
}

export function isTemplateSeededTextType(textType: string | undefined): boolean {
  return !!textType && TEMPLATE_SEEDED_TEXT_TYPES.has(textType);
}
